/**
 * The airport pad's geometry, shared by the thing that *draws* it and the thing that *stands* on it.
 *
 * ## The bug this file exists to close
 *
 * An airport is placed from OurAirports' surveyed threshold elevations. The terrain under it is
 * whatever SRTM sampled at 30 m posting, streamed as DEM tiles. Those two numbers are not the
 * same number, and nothing used to reconcile them: the pavement was drawn at the survey and the
 * flight model's ground came from the DEM.
 *
 * At KSFO the DEM runs 0.25–0.71 m *below* the pavement along the length of 10L. A C172's wheels
 * are 0.4 m across, so the aeroplane rested with its wheels entirely inside the tarmac and the
 * pilot's eye half a metre below the surface it was supposed to be looking along. It was visible
 * from the first frame of every session — the aeroplane looked sunk into the runway, because it
 * was.
 *
 * `runwayMesh.ts` already builds a flat *pad* at field elevation to hide the DEM's lumps under
 * the pavement. The fix is not to add new geometry, then; it is to make the flight model stand on
 * the pad that is already there. So the constants that define the pad live here, both modules
 * import them, and {@link runwaySurfaceElevationM} is the single answer to "what height is the
 * ground at this point" that the mesh and the gear now share. They cannot drift apart, because
 * there is only one of them.
 *
 * ## Why an equirectangular projection is good enough here, when it is not elsewhere
 *
 * The project is otherwise strict about geodesy — WGS84 in double precision, floating origin, the
 * curvature drop in the geometry (see `world/geodesy.ts`). This function deliberately uses a flat
 * local projection about the runway's low threshold instead, and that is a considered choice
 * rather than a shortcut:
 *
 *   - The domain is one runway. The longest in the database is under 5.5 km.
 *   - Over 5 km the equirectangular error against the true geodesic is under 2 mm, which is four
 *     orders of magnitude below the half-metre this function exists to fix, and well below the
 *     ±0.5 m the baked elevations are quantised to anyway.
 *   - It is called on the 120 Hz physics tick, for every runway of every nearby airport, and it
 *     must not allocate. A `LocalFrame` per query would.
 *
 * `updateFixed(dt)`: called from {@link WorldSystem.updateFixed}, at 120 Hz. Allocation-free.
 */

import type { Runway } from './db.js';

/** How far the flattened pad extends past the pavement, as a multiple of runway width. */
export const PAD_MARGIN = 3.5;

/** How far the pad's skirt drops to meet the terrain [m]. */
export const PAD_SKIRT_M = 120;

/**
 * How far the pavement quad is lifted above the pad [m].
 *
 * Six centimetres, and it exists so the runway wins the depth fight with its own apron. Small,
 * but *not* ignorable here: leaving it out would put the wheels 6 cm under the tarmac, which is
 * the same bug this file fixes, only eight times smaller and eight times harder to see.
 */
export const PAVEMENT_LIFT_M = 0.06;

/** Radius of the sphere the airport geometry is laid out on [m]. Matches `runwayMesh.ts`. */
const EARTH_RADIUS_M = 6_371_008.8;
const M_PER_DEG_LAT = (EARTH_RADIUS_M * Math.PI) / 180;
const DEG2RAD = Math.PI / 180;

/**
 * How far the pad extends past each threshold [m].
 *
 * A quarter of the runway's length, capped at 260 m. The cap is what stops a 4 km runway from
 * flattening a kilometre of terrain at each end; the fraction is what gives a 500 m strip an
 * apron proportionate to itself.
 */
export function padOverrunM(runway: Runway): number {
  return Math.min(260, runway.lengthM * 0.25);
}

/** Half the pad's width [m]. */
export function padHalfWidthM(runway: Runway): number {
  return runway.widthM * PAD_MARGIN * 0.5;
}

/**
 * The height of the drawn airport surface at a point [m MSL], or `NaN` if the point is not on it.
 *
 * `NaN` for "no answer" rather than `null`, to match `TerrainSystem.sampleGroundHeightM` — the
 * caller already branches on `Number.isNaN` for exactly this, and returning a boxed `null` on the
 * physics tick would allocate.
 *
 * The surface is the *pavement* over the runway rectangle and the *apron* outside it, which is
 * the same distinction the renderer makes and therefore the same one the wheels should feel.
 * Across the width the pad is flat, matching the mesh: a runway is crowned by a few centimetres
 * in reality and by nothing at all here.
 */
export function runwaySurfaceElevationM(
  runway: Runway,
  latitudeDeg: number,
  longitudeDeg: number,
): number {
  const mPerDegLon = M_PER_DEG_LAT * Math.cos(runway.lowLatitudeDeg * DEG2RAD);

  // The runway axis, in metres east and north of the low threshold.
  const axisEastM = (runway.highLongitudeDeg - runway.lowLongitudeDeg) * mPerDegLon;
  const axisNorthM = (runway.highLatitudeDeg - runway.lowLatitudeDeg) * M_PER_DEG_LAT;
  const axisLengthM = Math.hypot(axisEastM, axisNorthM);
  // A runway whose two ends are at the same place has no axis to project onto. The database has
  // a placement-quality flag for a reason; this is the degenerate tail of it.
  if (!(axisLengthM > 1)) return Number.NaN;
  const unitEast = axisEastM / axisLengthM;
  const unitNorth = axisNorthM / axisLengthM;

  // The query point, in the same frame.
  const pointEastM = (longitudeDeg - runway.lowLongitudeDeg) * mPerDegLon;
  const pointNorthM = (latitudeDeg - runway.lowLatitudeDeg) * M_PER_DEG_LAT;

  const alongM = pointEastM * unitEast + pointNorthM * unitNorth;
  const acrossM = Math.abs(-pointEastM * unitNorth + pointNorthM * unitEast);

  const overrunM = padOverrunM(runway);
  if (alongM < -overrunM || alongM > axisLengthM + overrunM) return Number.NaN;
  if (acrossM > padHalfWidthM(runway)) return Number.NaN;

  /*
   * The pad carries the runway's own slope straight through the overruns rather than levelling
   * off at the thresholds — which is what `buildPad` draws, and which matters at somewhere like
   * Courchevel where the runway climbs 100 m over 537 m and a flat overrun would be a cliff.
   *
   * Note the fraction is deliberately *not* clamped to 0…1.
   */
  const slopePerM = (runway.highElevationM - runway.lowElevationM) / axisLengthM;
  const padElevationM = runway.lowElevationM + slopePerM * alongM;

  const onPavement = acrossM <= runway.widthM * 0.5 && alongM >= 0 && alongM <= axisLengthM;
  return onPavement ? padElevationM + PAVEMENT_LIFT_M : padElevationM;
}

/**
 * The highest airport surface under a point across every runway of an airport, or `NaN`.
 *
 * Highest rather than first, because parallel runways at a big field have overlapping pads and
 * their thresholds are not at identical elevations. Taking the highest is what stops an aeroplane
 * on 10L from standing on 10R's apron and sinking through its own.
 */
export function airportSurfaceElevationM(
  runways: readonly Runway[],
  latitudeDeg: number,
  longitudeDeg: number,
): number {
  let best = Number.NaN;
  for (let i = 0; i < runways.length; i++) {
    const elevation = runwaySurfaceElevationM(runways[i], latitudeDeg, longitudeDeg);
    if (Number.isNaN(elevation)) continue;
    if (Number.isNaN(best) || elevation > best) best = elevation;
  }
  return best;
}
